import { useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, X, Loader2, Download, StopCircle } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import ExifReader from "exifreader";
import { AnalysisData } from "@/pages/Index";
import { Progress } from "@/components/ui/progress";

interface BulkImageUploaderProps {
  onAnalysisComplete: (results: Array<{ file: File; data: AnalysisData }>) => void;
  isAnalyzing: boolean;
  setIsAnalyzing: (analyzing: boolean) => void;
  onAnalyzeFiles?: (files: File[]) => Promise<void>;
}

export const BulkImageUploader = ({
  onAnalysisComplete,
  isAnalyzing,
  setIsAnalyzing,
  onAnalyzeFiles,
}: BulkImageUploaderProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [currentAnalyzingIndex, setCurrentAnalyzingIndex] = useState<number>(-1);
  const [analyzedCount, setAnalyzedCount] = useState<number>(0);
  const cancelRef = useRef<boolean>(false);

  const extractExifData = async (file: File) => {
    try {
      const tags = await ExifReader.load(file);
      return {
        make: tags.Make?.description,
        model: tags.Model?.description,
        dateTime: tags.DateTime?.description,
        focalLength: tags.FocalLength?.description,
        iso: tags.ISOSpeedRatings?.description,
        exposureTime: tags.ExposureTime?.description,
        fNumber: tags.FNumber?.description,
        gps: tags.GPSLatitude && tags.GPSLongitude ? {
          latitude: tags.GPSLatitude.description,
          longitude: tags.GPSLongitude.description,
        } : undefined,
      };
    } catch (error) {
      console.log("No EXIF data found");
      return undefined;
    }
  };

  const analyzeImage = async (file: File): Promise<AnalysisData> => {
    const exifData = await extractExifData(file);
    
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve) => {
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
    
    const imageUrl = await base64Promise;

    const { data, error } = await supabase.functions.invoke("analyze-image", {
      body: { image: imageUrl },
    });

    if (error) {
      if (error.message?.includes('429') || error.status === 429) {
        throw new Error('Rate limit exceeded. Please try again in a moment.');
      }
      if (error.message?.includes('402') || error.status === 402) {
        throw new Error('AI usage limit reached. Please contact support.');
      }
      throw error;
    }

    return {
      ...data,
      exif: exifData,
    };
  };

  const handleFilesSelect = useCallback((files: File[]) => {
    const validFiles = files.filter(file => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    const newPreviews = validFiles.map(file => URL.createObjectURL(file));
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
    setPreviews(prev => [...prev, ...newPreviews]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFilesSelect(files);
  }, [handleFilesSelect]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFilesSelect(files);
    }
  };

  const removeFile = (index: number) => {
    if (isAnalyzing && index === currentAnalyzingIndex) {
      toast.error("Cannot remove image currently being analyzed");
      return;
    }
    
    URL.revokeObjectURL(previews[index]);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
    
    // Adjust currentAnalyzingIndex if needed
    if (isAnalyzing && index < currentAnalyzingIndex) {
      setCurrentAnalyzingIndex(prev => prev - 1);
    }
  };

  const handleAnalyzeAll = async () => {
    if (selectedFiles.length === 0) return;

    cancelRef.current = false;
    setIsAnalyzing(true);
    setAnalyzedCount(0);
    const results: Array<{ file: File; data: AnalysisData }> = [];

    for (let i = 0; i < selectedFiles.length; i++) {
      if (cancelRef.current) {
        toast.info("Analysis cancelled");
        break;
      }

      const file = selectedFiles[i];
      setCurrentAnalyzingIndex(i);
      
      try {
        toast.info(`Analyzing ${i + 1}/${selectedFiles.length}: ${file.name}`);
        const data = await analyzeImage(file);
        results.push({ file, data });
        setAnalyzedCount(i + 1);
      } catch (error: any) {
        console.error(`Error analyzing ${file.name}:`, error);
        toast.error(`Failed to analyze ${file.name}: ${error.message}`);
      }
    }

    setIsAnalyzing(false);
    setCurrentAnalyzingIndex(-1);
    
    if (results.length > 0) {
      toast.success(`Successfully analyzed ${results.length} image(s)`);
      onAnalysisComplete(results);
    }
  };

  const handleStopAnalysis = () => {
    cancelRef.current = true;
    toast.info("Stopping analysis...");
  };

  const handleClear = () => {
    previews.forEach(url => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setPreviews([]);
  };

  return (
    <Card className="p-4 sm:p-6">
      <div className="space-y-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">Bulk Upload</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Upload multiple images at once for batch analysis
          </p>
        </div>

        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed border-border rounded-lg p-6 sm:p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
        >
          <input
            type="file"
            id="bulk-file-input"
            className="hidden"
            accept="image/*"
            multiple
            onChange={handleFileInput}
            disabled={isAnalyzing}
          />
          <label htmlFor="bulk-file-input" className="cursor-pointer">
            <Upload className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-base sm:text-lg font-medium text-foreground mb-2">
              Drop images here or click to browse
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Support for JPG, PNG, WEBP (max 10MB each)
            </p>
          </label>
        </div>

        {previews.length > 0 && (
          <>
            {isAnalyzing && (
              <div className="space-y-2 p-3 sm:p-4 bg-muted/50 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-foreground">
                    Analyzing: {analyzedCount}/{selectedFiles.length}
                  </span>
                  <span className="text-muted-foreground">
                    {Math.round((analyzedCount / selectedFiles.length) * 100)}%
                  </span>
                </div>
                <Progress value={(analyzedCount / selectedFiles.length) * 100} />
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 max-h-[300px] overflow-y-auto">
              {previews.map((preview, index) => (
                <div key={index} className="relative group">
                  <div className={`relative ${
                    isAnalyzing && index === currentAnalyzingIndex 
                      ? 'ring-2 ring-primary ring-offset-2' 
                      : isAnalyzing && index < currentAnalyzingIndex
                      ? 'opacity-60'
                      : ''
                  }`}>
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-24 sm:h-32 object-cover rounded-lg"
                    />
                    {isAnalyzing && index === currentAnalyzingIndex && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
                        <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 text-white animate-spin" />
                      </div>
                    )}
                    {isAnalyzing && index < currentAnalyzingIndex && (
                      <div className="absolute top-1 left-1 bg-primary text-primary-foreground rounded-full p-1">
                        <Download className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="absolute top-1 right-1 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                    disabled={isAnalyzing && index === currentAnalyzingIndex}
                    title={isAnalyzing && index === currentAnalyzingIndex ? "Cannot remove while analyzing" : "Remove"}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              {isAnalyzing ? (
                <Button
                  onClick={handleStopAnalysis}
                  variant="destructive"
                  className="flex-1 h-11 sm:h-12 text-sm sm:text-base"
                >
                  <StopCircle className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  Stop Analysis
                </Button>
              ) : (
                <Button
                  onClick={handleAnalyzeAll}
                  className="flex-1 h-11 sm:h-12 text-sm sm:text-base"
                >
                  <Download className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                  Analyze All ({selectedFiles.length})
                </Button>
              )}
              <Button
                onClick={handleClear}
                variant="outline"
                disabled={isAnalyzing}
                className="h-11 sm:h-12 text-sm sm:text-base"
              >
                Clear All
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};